/**
 * ReviewPreviewCard — VERBATIM port from
 * ~/FrontendFinal/Design/src/components/ReviewPreviewCard.tsx.
 *
 * The LARGE tool-preview card on the left of P4 ("Review your writeup"): 445x395,
 * a white plate with one large image tile, name + owner beneath. A DIFFERENT card
 * from the small PreviewCard (which holds four logos) — its own geometry, its own
 * component. The 15px plate inset, the non-centred tile offsets, and the text
 * rhythm are reverse-engineered SVG measurement — see the Design repo's header;
 * do not re-measure. Imports nothing, so no repoint.
 *
 * In the reskin `name`/`owner` come from the Vm; the tile remains a labelled
 * placeholder (the Design's TODO(dev): the real thumbnail is not carried here).
 *
 * BACKEND ADDITION, not part of the verbatim port. The Design's TODO(dev) above
 * is now answered: the "real thumbnail" does not exist and there is no route that
 * would produce one, so the plate holds GENERATED art instead — `FlowArt`, the
 * same deterministic diagram the small card shows, at 415x272 rather than
 * 214x128 and therefore at roughly twice the detail budget. This is the largest
 * box the flow is ever drawn in, which is the point of showing it on the last
 * step. Two measured values go with the stub they positioned and nothing else:
 * `pl-review-tile-x` / `pt-review-tile-y` were the tile's non-centred offsets,
 * and there is no longer a tile to offset. The 15px plate inset, the fixed 395
 * height and the text rhythm are untouched, and both new props are optional.
 */
import { FlowArt, flowArtLabel } from '../../../design/FlowArt';
import type { FlowShapeNode } from '@trailblazer/shared';

/**
 * The plate's own box, and therefore the art's viewBox — in real CSS px, straight
 * off `--review-plate-width` / `--review-plate-height` (445 less the 15px inset
 * on each side, by 272).
 *
 * The card is `w-full max-w-review-card`, so in a narrowed modal the plate is
 * narrower than 415 and `preserveAspectRatio="xMidYMid meet"` letterboxes the
 * difference — a paper margin at the sides, painted by `.fa`'s own background,
 * never a distorted diagram. At rest the margin is zero.
 */
const ART_W = 415;
const ART_H = 272;

export interface ReviewPreviewCardProps {
  /** The tool's own name. */
  name: string;
  /** The owning account. */
  owner: string;
  /**
   * The flow's structure — an encoded shape or decoded nodes. By P4 the manifest
   * has normally loaded, so this is the structural diagram; absent or unreadable
   * falls back to the emblem, which is exactly the case where there is no
   * connector count to pass either, so `known` is deliberately not wired here.
   */
  shape?: string | readonly FlowShapeNode[] | null;
  className?: string;
}

export function ReviewPreviewCard({ name, owner, shape, className = '' }: ReviewPreviewCardProps) {
  return (
    <div
      className={[
        // 445 wide at rest (max-w-review-card), FLUID below that so it never
        // overflows a narrowed modal. Height fixed 395 (r8 = rounded-md).
        'flex h-review-card w-full max-w-review-card min-w-0 flex-col rounded-md bg-accent-subtle',
        // The 15px plate inset is the card's own top/left/right padding; the
        // bottom is left to the fixed height (the text band lives there).
        'px-review-plate-inset pt-review-plate-inset',
        className,
      ].join(' ')}
    >
      {/* The white plate. Fixed 415x272; the 15 inset comes from the card padding
          above, so the plate is a plain full-width child. The art fills it, so
          `overflow-hidden` clips the svg's square corners to the r8 radius. */}
      <div className="h-review-plate w-full overflow-hidden rounded-md bg-surface">
        <FlowArt
          shape={shape}
          name={name}
          width={ART_W}
          height={ART_H}
          // Announced, not ornamental — see PreviewCard for the reasoning. Null
          // when there is no readable structure, which falls back to aria-hidden.
          label={flowArtLabel(shape)}
        />
      </div>

      {/* Name and owner, below the plate. pt-review-text is the plate-bottom →
          name-box-top gap; the two lines then stack with gap-review-text-row
          between their boxes. Inset pl-review-text-x from the plate left. */}
      <div className="flex min-w-0 flex-col gap-review-text-row pl-review-text-x pt-review-text">
        <p className="truncate text-sm font-medium text-ink">{name}</p>
        <p className="truncate text-sm font-light text-ink">{owner}</p>
      </div>
    </div>
  );
}
